import * as React from 'react';

import {
  Button,
  Col,
  ConfirmPopoverButton,
  Row,
  SearchField,
  cn,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue } from 'jotai';
import {
  LucideChevronUp,
  LucideShield,
  LucideTrash2,
  LucideUsers,
} from 'lucide-react';

import {
  type FacilityRoleRecord,
  type FacilityRoleUserRecord,
  useFacilityRolesQuery,
} from './_graphql/useFacilityRolesQuery';
import { useUserRoleRevokeMutation } from './_graphql/useUserRoleRevokeMutation';
import { AddUsersToRolePopover } from './AddUsersToRolePopover';

import {
  CopyToClipboard,
  DetailPanel,
  EmptyDetailState,
  FormFooter,
  PermissionsEditor,
  SelectionListItem,
  SettingsContent,
  TwoPanelSelectionLayout,
} from '~/modules/settings/_components/_shared';
import { formatDate } from '~/modules/settings/_components/_shared/utils';
import { allPermissionModules } from '~/modules/settings/_generated/permissions';
import {
  $currentFacility,
  $facilityDetailsSelectedRoleId,
} from '~/modules/settings/_state/facilityDetailsForm.state';
import { useSettingsLocation } from '~/modules/settings/_state/settingsModule.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';

interface FacilityRoleDetailPanelProps {
  role: FacilityRoleRecord;
  users: FacilityRoleUserRecord[];
  facilityId: string;
  onGoToRoleSettings: () => void;
  onRemoveUser: (user: FacilityRoleUserRecord) => Promise<void>;
}

const FacilityRoleDetailPanel: React.FC<FacilityRoleDetailPanelProps> = ({
  role,
  users,
  facilityId,
  onGoToRoleSettings,
  onRemoveUser,
}) => {
  const t = useTranslations();
  const [userSearchTerm, setUserSearchTerm] = React.useState('');
  const [isUsersExpanded, setIsUsersExpanded] = React.useState(true);

  const filteredUsers = React.useMemo(() => {
    const sorted = [...users].sort((a, b) => a.name.localeCompare(b.name));
    if (!userSearchTerm) return sorted;
    const lower = userSearchTerm.toLowerCase();
    return sorted.filter(
      (u) =>
        u.name.toLowerCase().includes(lower) ||
        u.email.toLowerCase().includes(lower),
    );
  }, [users, userSearchTerm]);

  const permissionsValue = React.useMemo(() => {
    const value: Record<string, boolean> = {};
    role.permissions.forEach((key) => {
      value[key] = true;
    });
    return value;
  }, [role.permissions]);

  const lastUpdatedText = role.updatedAt
    ? `Last updated on ${formatDate(role.updatedAt)}${role.updatedBy ? ` by ${role.updatedBy}` : ''}`
    : '';

  return (
    <DetailPanel
      header={
        <div className="px-8 pt-5">
          <Row className="items-start justify-between">
            <Col className="gap-0.5">
              <span className="text-lg font-bold text-neutral">
                {role.name}
              </span>
              <span className="text-sm text-neutral/60">
                {lastUpdatedText || '\u00A0'}
              </span>
            </Col>
            <Button variant="outline" onPress={onGoToRoleSettings}>
              {t.teamAndPermissions.facilities.details.actions.goToRoleSettings}
            </Button>
          </Row>
        </div>
      }
      contentClassName="gap-3"
      footer={
        <FormFooter
          isDiscardDisabled
          isSaveDisabled
          discardLabel={t.teamAndPermissions.actions.discard}
          saveLabel={t.teamAndPermissions.actions.save}
        />
      }
      className="@FacilityRoleDetailPanel"
    >
      <div className="border-b border-neutral/10">
        <div className="flex items-center gap-1.5 py-2.5">
          <button
            type="button"
            className="flex size-5 items-center justify-center rounded bg-neutral/10"
            onClick={() => setIsUsersExpanded((prev) => !prev)}
            aria-label={isUsersExpanded ? 'Collapse users' : 'Expand users'}
          >
            <LucideChevronUp
              size={16}
              className={cn(
                'text-neutral/60 transition-transform',
                !isUsersExpanded && 'rotate-180',
              )}
            />
          </button>
          <span className="font-bold text-neutral/80">
            {t.teamAndPermissions.facilities.details.sections.users}{' '}
            <span className="font-normal text-neutral/60">
              ({users.length})
            </span>
          </span>
        </div>

        {isUsersExpanded && (
          <Col className="gap-3 pt-2 pl-8">
            <Row gap2>
              <SearchField
                value={userSearchTerm}
                onChange={setUserSearchTerm}
                placeholder={t.teamAndPermissions.search.placeholder}
                className="h-8"
              />
              <AddUsersToRolePopover
                roleId={role.id}
                facilityId={facilityId}
                existingUserIds={new Set(users.map((u) => u.id))}
              />
            </Row>

            <Col>
              {filteredUsers.map((user, index) => (
                <React.Fragment key={user.id}>
                  {index > 0 && <div className="border-t border-neutral/10" />}
                  <Row className="items-center gap-3 py-2">
                    <span className="w-[179px] shrink-0 text-sm font-medium text-neutral">
                      {user.name}
                    </span>
                    <div className="min-w-0 flex-1">
                      {user.email ? (
                        <CopyToClipboard
                          text={user.email}
                          copyLabel="Copy email"
                          copiedLabel="Email copied"
                        />
                      ) : (
                        <span className="text-sm text-neutral/40">-</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      {user.phoneNumber ? (
                        <CopyToClipboard
                          text={user.phoneNumber}
                          copyLabel="Copy phone"
                          copiedLabel="Phone copied"
                        />
                      ) : (
                        <span className="text-sm text-neutral/40">-</span>
                      )}
                    </div>
                    <ConfirmPopoverButton
                      onConfirm={() => onRemoveUser(user)}
                      message={t.teamAndPermissions.roles.details.users.removeFromRoleDialog.description(
                        user.name,
                      )}
                      confirmLabel={
                        t.teamAndPermissions.roles.details.users
                          .removeFromRoleDialog.confirm
                      }
                      cancelLabel={
                        t.teamAndPermissions.roles.details.users
                          .removeFromRoleDialog.cancel
                      }
                      title={t.teamAndPermissions.roles.details.users.removeFromRoleDialog.title(
                        user.name,
                      )}
                      placement="bottom end"
                      contentClassName="max-w-md"
                      renderTrigger={(props) => (
                        <Button
                          {...props}
                          size="icon"
                          variant="outline"
                          color="failure"
                          icon={<LucideTrash2 className="size-4" />}
                          aria-label={`Remove ${user.name}`}
                        />
                      )}
                    />
                  </Row>
                </React.Fragment>
              ))}
            </Col>
          </Col>
        )}
      </div>

      <PermissionsEditor
        modules={allPermissionModules}
        value={permissionsValue}
        readOnly
        collapsible
      />
    </DetailPanel>
  );
};

export const FacilityRolesTab = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const currentFacility = useAtomValue($currentFacility);
  const [selectedRoleId, setSelectedRoleId] = useAtom(
    $facilityDetailsSelectedRoleId,
  );

  const { toast } = useToast();
  const revokeMutation = useUserRoleRevokeMutation();

  const [searchValue, setSearchValue] = React.useState('');

  const { data } = useFacilityRolesQuery({
    facilityId: currentFacility?.id ?? '',
    limit: 1000,
    offset: 0,
  });

  const roles = data?.roles ?? [];
  const usersMap = data?.users ?? new Map<string, FacilityRoleUserRecord[]>();

  React.useEffect(() => {
    if (!selectedRoleId && roles.length > 0) {
      setSelectedRoleId(roles[0].id);
    }
  }, [selectedRoleId, roles, setSelectedRoleId]);

  const filteredRoles = React.useMemo(
    () =>
      roles.filter((role) =>
        role.name.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [roles, searchValue],
  );

  const selectedRoleUsers = selectedRoleId
    ? (usersMap.get(selectedRoleId) ?? [])
    : [];

  const handleGoToRoleSettings = (roleId: string) => {
    setLocation(`/team-and-permissions/roles/${roleId}` as never);
  };

  const handleRemoveUser = async (user: FacilityRoleUserRecord) => {
    try {
      await revokeMutation.mutateAsync({
        assignmentId: user.assignmentId,
      });
      toast({
        title:
          t.teamAndPermissions.roles.details.users.removeFromRoleDialog.success,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title:
          t.teamAndPermissions.roles.details.users.removeFromRoleDialog.error,
        description: err instanceof Error ? err.message : undefined,
        variant: 'error',
      });
    }
  };

  return (
    <SettingsContent size="full" className="@FacilityRolesTab h-full">
      <TwoPanelSelectionLayout
        items={filteredRoles}
        selectedId={selectedRoleId}
        onSelect={setSelectedRoleId}
        getItemId={(role) => role.id}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Search"
        leftPanelWidth={240}
        renderItem={(role, isSelected) => (
          <div className="px-2">
            <SelectionListItem
              name={role.name}
              tagIcon={<LucideUsers size={14} />}
              tagCount={role.userCount}
              isSelected={isSelected}
              className={cn(
                role.userCount === 0 && !isSelected && 'opacity-60',
              )}
            />
          </div>
        )}
        renderDetail={(role) =>
          role ? (
            <FacilityRoleDetailPanel
              role={role}
              users={selectedRoleUsers}
              facilityId={currentFacility?.id ?? ''}
              onGoToRoleSettings={() => handleGoToRoleSettings(role.id)}
              onRemoveUser={handleRemoveUser}
            />
          ) : (
            <EmptyDetailState
              icon={<LucideShield />}
              title={t.teamAndPermissions.facilities.details.roles.emptyState}
              subtitle={
                t.teamAndPermissions.facilities.details.roles.emptyStateSubtitle
              }
            />
          )
        }
      />
    </SettingsContent>
  );
};
